// ---------------------------------------------------------------------------
// Design library view model — the pure derivation behind the Design tab's
// "Tokens" and "Components" screens.
//
// Everything here reads what the import already extracted from the `.fig`
// file and reshapes it for reading. Nothing is invented: when the file did
// not carry a fact, the model says so with an explicit `null`/empty value and
// the screens render an honest "not extracted" line instead of a placeholder.
// ---------------------------------------------------------------------------

import type {
  OpenPencilComponentPropertyDefinitionFact,
  OpenPencilComponentSetFact,
  OpenPencilFigmaSourceBundle,
  OpenPencilSurfaceCandidateFact,
  OpenPencilSurfacePreviewArtifact,
} from "./openpencil-figma-source";

// --- tokens ----------------------------------------------------------------

export interface SourceTokenModeValue {
  modeId: string;
  modeName: string;
  isDefault: boolean;
  /** The literal value this mode carries, already stringified for display. */
  display: string | null;
  /** Present only when the value is a renderable colour. */
  swatch: string | null;
  /** Set when the mode resolves through another variable instead of a value. */
  aliasName: string | null;
}

export interface SourceTokenUsageRef {
  componentSetId: string;
  componentSetName: string;
  /** How many nodes inside that component matched this variable's colour. */
  usageCount: number;
  confidence: number;
  evidenceKind: string;
}

export interface SourceTokenRow {
  id: string;
  name: string;
  /** Trailing segment of a `a/b/c` style variable name — the readable label. */
  shortName: string;
  collectionId: string;
  collectionName: string;
  tokenType: string;
  swatch: string | null;
  displayValue: string | null;
  description: string;
  hiddenFromPublishing: boolean;
  modes: SourceTokenModeValue[];
  usedBy: SourceTokenUsageRef[];
  usageTotal: number;
}

export interface SourceTokenGroup {
  /** Stable key: one group per (collection, variable type) pair. */
  key: string;
  collectionId: string;
  collectionName: string;
  /**
   * `collectionName`, plus the file's own collection id when another
   * collection shares that name — this file has four collections all called
   * "화면 모드", and identical headings would be unreadable.
   */
  collectionLabel: string;
  tokenType: string;
  modeNames: string[];
  tokens: SourceTokenRow[];
}

export interface SourceColorSample {
  id: string;
  label: string;
  value: string;
  swatch: string | null;
  usageCount: number;
}

export interface SourceTokenIndex {
  groups: SourceTokenGroup[];
  totalTokens: number;
  typeCounts: Record<string, number>;
  modeNames: string[];
  colorSamples: SourceColorSample[];
  /**
   * `false` when this file carried no component data at all, which is the
   * difference between "nothing uses this variable" and "we never looked".
   */
  usageEvidenceAvailable: boolean;
  /** How many variables were matched to at least one component. */
  tokensWithUsage: number;
  /** The evidence kinds behind every usage link, for an honest caption. */
  usageEvidenceKinds: string[];
}

const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

function asSwatch(value: unknown): string | null {
  return typeof value === "string" && HEX_COLOR.test(value.trim())
    ? value.trim()
    : null;
}

function asDisplay(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  const text = String(value).trim();
  return text.length > 0 ? text : null;
}

/** `base/base/blue/500` reads better as `blue/500` next to its full path. */
function shortenTokenName(name: string): string {
  const parts = name.split("/").filter(Boolean);
  return parts.length <= 2 ? name : parts.slice(-2).join("/");
}

function buildTokenUsageIndex(
  componentSets: readonly OpenPencilComponentSetFact[],
): Map<string, SourceTokenUsageRef[]> {
  const index = new Map<string, SourceTokenUsageRef[]>();
  for (const componentSet of componentSets) {
    for (const candidate of componentSet.visualTokenCandidates ?? []) {
      const refs = index.get(candidate.variableId) ?? [];
      refs.push({
        componentSetId: componentSet.id,
        componentSetName: componentSet.name,
        usageCount: candidate.usageCount,
        confidence: candidate.confidence,
        evidenceKind: candidate.evidenceKind,
      });
      index.set(candidate.variableId, refs);
    }
  }
  for (const refs of index.values()) {
    refs.sort((left, right) => right.usageCount - left.usageCount);
  }
  return index;
}

export function buildSourceTokenIndex(
  bundle: OpenPencilFigmaSourceBundle | null,
): SourceTokenIndex {
  const facts = bundle?.sourceFacts ?? null;
  const tokens = facts?.designTokens ?? [];
  const componentSets = facts?.uiOntology?.componentSets ?? [];
  const usageIndex = buildTokenUsageIndex(componentSets);
  const collections = new Map(
    (facts?.variableCollections ?? []).map((item) => [item.id, item] as const),
  );

  const groups = new Map<string, SourceTokenGroup>();
  const typeCounts: Record<string, number> = {};
  const modeNames = new Set<string>();
  let tokensWithUsage = 0;
  const usageEvidenceKinds = new Set<string>();

  for (const token of tokens) {
    const usedBy = usageIndex.get(token.id) ?? [];
    if (usedBy.length > 0) tokensWithUsage += 1;
    for (const ref of usedBy) usageEvidenceKinds.add(ref.evidenceKind);

    const declaredModes =
      token.modes.length > 0
        ? token.modes
        : (collections.get(token.collectionId)?.modes ?? []);
    const valuesByMode = new Map(
      token.valuesByMode.map((entry) => [entry.modeId, entry] as const),
    );
    const modes: SourceTokenModeValue[] = declaredModes.map((mode) => {
      const entry = valuesByMode.get(mode.modeId);
      modeNames.add(mode.name);
      return {
        modeId: mode.modeId,
        modeName: mode.name,
        isDefault: mode.isDefault,
        display: asDisplay(entry?.value),
        swatch: asSwatch(entry?.value),
        aliasName: entry?.aliasName ?? null,
      };
    });

    // The top-level `value` drops alpha (a fully transparent variable arrives
    // as `#000000`), so the default mode's own value wins whenever the file
    // recorded one. Otherwise the row would paint solid black for a colour the
    // file says is invisible.
    const defaultMode = modes.find((mode) => mode.isDefault);
    const row: SourceTokenRow = {
      id: token.id,
      name: token.name,
      shortName: shortenTokenName(token.name),
      collectionId: token.collectionId,
      collectionName: token.collectionName,
      tokenType: token.tokenType,
      swatch: defaultMode?.swatch ?? asSwatch(token.value),
      displayValue: defaultMode?.display ?? asDisplay(token.value),
      description: token.description ?? "",
      hiddenFromPublishing: token.hiddenFromPublishing ?? false,
      modes,
      usedBy,
      usageTotal: usedBy.reduce((total, ref) => total + ref.usageCount, 0),
    };

    typeCounts[token.tokenType] = (typeCounts[token.tokenType] ?? 0) + 1;
    const key = `${token.collectionId}::${token.tokenType}`;
    const group = groups.get(key);
    if (group) {
      group.tokens.push(row);
      for (const mode of modes) {
        if (!group.modeNames.includes(mode.modeName))
          group.modeNames.push(mode.modeName);
      }
    } else {
      groups.set(key, {
        key,
        collectionId: token.collectionId,
        collectionName: token.collectionName,
        collectionLabel: token.collectionName,
        tokenType: token.tokenType,
        modeNames: modes.map((mode) => mode.modeName),
        tokens: [row],
      });
    }
  }

  // Disambiguate headings only where the file itself reuses a collection name.
  const collectionIdsByName = new Map<string, Set<string>>();
  for (const group of groups.values()) {
    const ids = collectionIdsByName.get(group.collectionName) ?? new Set();
    ids.add(group.collectionId);
    collectionIdsByName.set(group.collectionName, ids);
  }
  for (const group of groups.values()) {
    if ((collectionIdsByName.get(group.collectionName)?.size ?? 0) > 1) {
      group.collectionLabel = `${group.collectionName} (${group.collectionId})`;
    }
  }

  const colorSamples: SourceColorSample[] = (
    facts?.visualTokenEvidence ?? []
  ).map((sample) => ({
    id: sample.id,
    label: sample.label,
    value: sample.value,
    swatch: asSwatch(sample.value),
    usageCount: sample.usageCount,
  }));

  return {
    groups: [...groups.values()].sort(
      (left, right) =>
        right.tokens.length - left.tokens.length ||
        left.collectionName.localeCompare(right.collectionName),
    ),
    totalTokens: tokens.length,
    typeCounts,
    modeNames: [...modeNames],
    colorSamples,
    usageEvidenceAvailable: componentSets.length > 0,
    tokensWithUsage,
    usageEvidenceKinds: [...usageEvidenceKinds],
  };
}

/** Case-insensitive match over the variable name, value, and collection. */
export function filterSourceTokenGroups(
  groups: readonly SourceTokenGroup[],
  query: string,
): SourceTokenGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...groups];
  return groups
    .map((group) => ({
      ...group,
      tokens: group.tokens.filter((token) => {
        if (token.name.toLowerCase().includes(needle)) return true;
        if (token.collectionName.toLowerCase().includes(needle)) return true;
        if (token.displayValue?.toLowerCase().includes(needle)) return true;
        return token.modes.some((mode) =>
          mode.display?.toLowerCase().includes(needle),
        );
      }),
    }))
    .filter((group) => group.tokens.length > 0);
}

export function countSourceTokens(groups: readonly SourceTokenGroup[]): number {
  return groups.reduce((total, group) => total + group.tokens.length, 0);
}

// --- components ------------------------------------------------------------

export interface SourceScreenRow {
  surfaceId: string;
  name: string;
  pageId: string;
  pageName: string;
  width: number;
  height: number;
  classification: "mobile" | "non-mobile";
  sectionId: string | null;
  sectionName: string | null;
  nodeId: string;
  preview: OpenPencilSurfacePreviewArtifact | null;
  previewFailure: string | null;
  /**
   * Layers inside the page this screen belongs to. The import counts layers
   * per page, never per screen, so the screen-level number stays unavailable
   * rather than being guessed from the page total.
   */
  pageLayerCount: number | null;
  /** Component instances observed on this screen's page. */
  pageComponentInstanceCount: number | null;
}

export interface SourceComponentPart {
  name: string;
  nodeTypes: string[];
  observedCount: number;
  /** `null` when the extractor did not record how deep the part sits. */
  maxDepth: number | null;
}

export interface SourceComponentSlot {
  propertyName: string;
  semanticStatus: string;
  editableInCogo: boolean;
}

export interface SourceComponentTokenRef {
  variableId: string;
  variableName: string;
  value: string;
  usageCount: number;
  confidence: number;
  evidenceKind: string;
  /** True when this variable is also one of the file's declared variables. */
  declared: boolean;
}

export interface SourceComponentRow {
  id: string;
  name: string;
  pageName: string;
  description: string;
  isPublishable: boolean;
  variantCount: number;
  sampledVariantCount: number;
  properties: OpenPencilComponentPropertyDefinitionFact[];
  propertyTypeCounts: Record<string, number>;
  observedVariantValues: Record<string, string[]>;
  parts: SourceComponentPart[];
  /** Layers walked while deriving the parts above — a real, per-component count. */
  inspectedNodeCount: number | null;
  partsTruncated: boolean;
  slotStatus: "not-observed" | "instance-swap-observed";
  slots: SourceComponentSlot[];
  tokenRefs: SourceComponentTokenRef[];
  variantCoverageComplete: boolean | null;
}

export interface SourceComponentCatalog {
  screens: SourceScreenRow[];
  components: SourceComponentRow[];
  /** Total components in the file — `components` may be a sampled subset. */
  declaredComponentSetCount: number | null;
  declaredComponentCount: number | null;
  declaredInstanceCount: number | null;
  previewsRendered: number;
  previewsRequested: number;
  /** `false` when the import produced no component data for this file. */
  componentEvidenceAvailable: boolean;
}

function tallyPropertyTypes(
  properties: readonly OpenPencilComponentPropertyDefinitionFact[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const property of properties) {
    counts[property.type] = (counts[property.type] ?? 0) + 1;
  }
  return counts;
}

export function buildSourceComponentCatalog(
  bundle: OpenPencilFigmaSourceBundle | null,
): SourceComponentCatalog {
  const facts = bundle?.sourceFacts ?? null;
  const surfaces: readonly OpenPencilSurfaceCandidateFact[] =
    facts?.surfaceCandidates ?? [];
  const manifest = bundle?.visualPreviewManifest ?? null;
  const previewBySurface = new Map(
    (manifest?.items ?? []).map((item) => [item.surfaceId, item] as const),
  );
  const failureBySurface = new Map(
    (manifest?.failures ?? []).map(
      (item) => [item.surfaceId, item.message] as const,
    ),
  );
  const pageLayerCounts = new Map(
    (facts?.pages ?? []).map((page) => [page.id, page.nodeCount] as const),
  );
  const sectionNames = new Map(
    (facts?.storySections ?? []).map(
      (section) => [section.id, section.name] as const,
    ),
  );
  const instancesByPage = new Map<string, number>();
  for (const instance of facts?.uiOntology?.componentInstances ?? []) {
    instancesByPage.set(
      instance.pageId,
      (instancesByPage.get(instance.pageId) ?? 0) + 1,
    );
  }
  const hasInstanceFacts =
    (facts?.uiOntology?.componentInstances ?? []).length > 0;
  const declaredVariableIds = new Set(
    (facts?.designTokens ?? []).map((token) => token.id),
  );

  const screens: SourceScreenRow[] = surfaces.map((surface) => ({
    surfaceId: surface.id,
    name: surface.name,
    pageId: surface.pageId,
    pageName: surface.pageName,
    width: surface.width,
    height: surface.height,
    classification: surface.classification,
    sectionId: surface.sectionId ?? null,
    sectionName: surface.sectionId
      ? (sectionNames.get(surface.sectionId) ?? null)
      : null,
    nodeId: surface.nodeId,
    preview: previewBySurface.get(surface.id) ?? null,
    previewFailure: failureBySurface.get(surface.id) ?? null,
    pageLayerCount: pageLayerCounts.get(surface.pageId) ?? null,
    pageComponentInstanceCount: hasInstanceFacts
      ? (instancesByPage.get(surface.pageId) ?? 0)
      : null,
  }));

  const componentSets = facts?.uiOntology?.componentSets ?? [];
  const components: SourceComponentRow[] = componentSets.map((componentSet) => {
    const coverage = componentSet.anatomyCoverage;
    return {
      id: componentSet.id,
      name: componentSet.name,
      pageName: componentSet.pageName,
      description: componentSet.symbolDescription ?? "",
      isPublishable: componentSet.isPublishable,
      variantCount: componentSet.variantCount,
      sampledVariantCount: componentSet.variantSamples.length,
      properties: [...componentSet.propertyDefinitions],
      propertyTypeCounts: tallyPropertyTypes(componentSet.propertyDefinitions),
      observedVariantValues: componentSet.observedVariantValues ?? {},
      parts: (componentSet.anatomyCandidates ?? []).map((candidate) => ({
        name: candidate.name,
        nodeTypes: [...candidate.nodeTypes],
        observedCount: candidate.observedCount,
        maxDepth: candidate.maxDepth ?? null,
      })),
      inspectedNodeCount: coverage?.visitedNodeCount ?? null,
      partsTruncated: Boolean(
        coverage?.traversalTruncated || coverage?.candidateOutputTruncated,
      ),
      slotStatus: componentSet.slotEvidenceStatus,
      slots: (componentSet.slotCandidates ?? []).map((slot) => ({
        propertyName: slot.propertyName,
        semanticStatus: slot.semanticStatus,
        editableInCogo: slot.editableInCogo,
      })),
      tokenRefs: (componentSet.visualTokenCandidates ?? []).map((candidate) => ({
        variableId: candidate.variableId,
        variableName: candidate.variableName,
        value: candidate.value,
        usageCount: candidate.usageCount,
        confidence: candidate.confidence,
        evidenceKind: candidate.evidenceKind,
        declared: declaredVariableIds.has(candidate.variableId),
      })),
      variantCoverageComplete:
        componentSet.variantCoverage?.completeValueCoverage ?? null,
    };
  });

  return {
    screens,
    components,
    declaredComponentSetCount: facts?.uiOntology?.counts.componentSets ?? null,
    declaredComponentCount: facts?.uiOntology?.counts.components ?? null,
    declaredInstanceCount: facts?.uiOntology?.counts.instances ?? null,
    previewsRendered: manifest?.rendered ?? 0,
    previewsRequested: manifest?.requested ?? surfaces.length,
    componentEvidenceAvailable: Boolean(facts?.uiOntology),
  };
}

export function filterSourceScreens(
  screens: readonly SourceScreenRow[],
  query: string,
): SourceScreenRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...screens];
  return screens.filter(
    (screen) =>
      screen.name.toLowerCase().includes(needle) ||
      screen.pageName.toLowerCase().includes(needle) ||
      (screen.sectionName ?? "").toLowerCase().includes(needle) ||
      `${screen.width}x${screen.height}`.includes(needle),
  );
}

export function filterSourceComponents(
  components: readonly SourceComponentRow[],
  query: string,
): SourceComponentRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...components];
  return components.filter(
    (component) =>
      component.name.toLowerCase().includes(needle) ||
      component.description.toLowerCase().includes(needle) ||
      component.properties.some((property) =>
        property.name.toLowerCase().includes(needle),
      ),
  );
}

/** `360 × 800` with thousands separators, matching the canvas frame labels. */
export function formatScreenSize(width: number, height: number): string {
  return `${Math.round(width).toLocaleString()} × ${Math.round(height).toLocaleString()}`;
}
